import { useTranslation } from 'react-i18next';
import { Badge, Box, Group, Stack, Text, Tooltip } from '@mantine/core';

import {
  CustomerAgreementMetadataType,
  EmployeeAgreementMetadataType,
  LeaseMetadataType,
} from '@app-types/blackroom-api';
import { SectionContentProps } from '../types';
import * as classes from './styles/index.css';

const EMPTY_VALUE = '—';

type MetadataField = {
  label: string;
  value?: string | null;
};

function MetadataRow({ label, value }: MetadataField) {
  if (!value) {
    return (
      <Group>
        <Text>{label}</Text>
        <Text c="dimmed">{EMPTY_VALUE}</Text>
      </Group>
    );
  }

  if (value.length > 50) {
    return (
      <Group>
        <Text>{label}</Text>
        <Tooltip label={value} multiline maw={300} withArrow>
          <Text truncate maw={180}>
            {value}
          </Text>
        </Tooltip>
      </Group>
    );
  }

  return (
    <Group>
      <Text>{label}</Text>
      <Text>{value}</Text>
    </Group>
  );
}

function BooleanRow({ label, value }: MetadataField) {
  const { t } = useTranslation();

  if (!value) {
    return (
      <Group>
        <Text>{label}</Text>
        <Text c="dimmed">{EMPTY_VALUE}</Text>
      </Group>
    );
  }

  const isYes = value.toLowerCase() === 'yes' || value.toLowerCase() === 'true';

  return (
    <Group>
      <Text>{label}</Text>
      <Badge
        color={isYes ? 'green' : 'red'}
        variant="filled"
        size="sm"
        c="white"
      >
        {isYes ? t('Common:yes') : t('Common:no')}
      </Badge>
    </Group>
  );
}

function CustomerAgreementMetadata({
  metadata,
}: {
  metadata: CustomerAgreementMetadataType;
}) {
  const { t } = useTranslation();

  return (
    <Stack className={classes.metaDataTable} gap={0}>
      <MetadataRow
        label={t('DueDiligence:CustomerAgreementsTable.customer')}
        value={metadata.customer}
      />
      <MetadataRow
        label={t('DueDiligence:CustomerAgreementsTable.startDate')}
        value={metadata.startDate}
      />
      <MetadataRow
        label={t('DueDiligence:CustomerAgreementsTable.endDate')}
        value={metadata.endDate}
      />
      <MetadataRow
        label={t('DueDiligence:CustomerAgreementsTable.terminationClause')}
        value={metadata.terminationClause}
      />
      <MetadataRow
        label={t(
          'DueDiligence:CustomerAgreementsTable.earlyTerminationIndemnity',
        )}
        value={metadata.earlyTerminationIndemnity}
      />
      <MetadataRow
        label={t('DueDiligence:CustomerAgreementsTable.limitationOfLiability')}
        value={metadata.limitationOfLiability}
      />
      <BooleanRow
        label={t('DueDiligence:CustomerAgreementsTable.changeOfControl')}
        value={metadata.changeOfControl}
      />
      <MetadataRow
        label={t('DueDiligence:CustomerAgreementsTable.exclusivity')}
        value={metadata.exclusivity}
      />
      <MetadataRow
        label={t('DueDiligence:CustomerAgreementsTable.contractValue')}
        value={metadata.contractValue}
      />
    </Stack>
  );
}

function EmployeeAgreementMetadata({
  metadata,
}: {
  metadata: EmployeeAgreementMetadataType;
}) {
  const { t } = useTranslation();

  return (
    <Stack className={classes.metaDataTable} gap={0}>
      <MetadataRow
        label={t('DueDiligence:EmployeeAgreementsTable.employee')}
        value={metadata.employee}
      />
      <MetadataRow
        label={t('DueDiligence:EmployeeAgreementsTable.jobTitle')}
        value={metadata.jobTitle}
      />
      <MetadataRow
        label={t('DueDiligence:EmployeeAgreementsTable.hiringDate')}
        value={metadata.hiringDate}
      />
      <MetadataRow
        label={t('DueDiligence:EmployeeAgreementsTable.baseSalary')}
        value={metadata.baseSalary}
      />
      <MetadataRow
        label={t('DueDiligence:EmployeeAgreementsTable.ote')}
        value={metadata.ote}
      />
      <BooleanRow
        label={t('DueDiligence:EmployeeAgreementsTable.nonCompete')}
        value={metadata.nonCompete}
      />
      <BooleanRow
        label={t('DueDiligence:EmployeeAgreementsTable.nonSolicitation')}
        value={metadata.nonSolicitation}
      />
      <BooleanRow
        label={t('DueDiligence:EmployeeAgreementsTable.ipClause')}
        value={metadata.ipClause}
      />
      <MetadataRow
        label={t('DueDiligence:EmployeeAgreementsTable.governingLaw')}
        value={metadata.governingLaw}
      />
    </Stack>
  );
}

function LeaseMetadata({ metadata }: { metadata: LeaseMetadataType }) {
  const { t } = useTranslation();

  return (
    <Stack className={classes.metaDataTable} gap={0}>
      <MetadataRow
        label={t('DueDiligence:LeasesTable.address')}
        value={metadata.address}
      />
      <MetadataRow
        label={t('DueDiligence:LeasesTable.term')}
        value={metadata.term}
      />
      <MetadataRow
        label={t('DueDiligence:LeasesTable.earlyTerminationAndRenewal')}
        value={metadata.earlyTerminationAndRenewal}
      />
      <MetadataRow
        label={t('DueDiligence:LeasesTable.annualRent')}
        value={metadata.annualRent}
      />
      <MetadataRow
        label={t('DueDiligence:LeasesTable.increaseAnnual')}
        value={metadata.increaseAnnual}
      />
      <MetadataRow
        label={t('DueDiligence:LeasesTable.geoRisks')}
        value={metadata.geoRisks}
      />
    </Stack>
  );
}

function SectionMetadata({ resource }: SectionContentProps) {
  const { t } = useTranslation();

  const { metadata } = resource;

  if (!metadata || metadata.__typename === 'GenericDocumentMetadataType') {
    return (
      <Box px="md">
        <Text size="sm" c="dimmed" ta="center" py="sm">
          {t('Resource:ResourceMenu.sections.metadata.empty')}
        </Text>
      </Box>
    );
  }

  return (
    <Box px="md">
      <Stack gap="xs">
        {metadata.__typename === 'CustomerAgreementMetadataType' && (
          <>
            <Text size="sm" fw={600}>
              {t('DueDiligence:PageDueDiligence.tabCustomerAgreements')}
            </Text>
            <CustomerAgreementMetadata metadata={metadata} />
          </>
        )}
        {metadata.__typename === 'EmployeeAgreementMetadataType' && (
          <>
            <Text size="sm" fw={600}>
              {t('DueDiligence:PageDueDiligence.tabEmployeeAgreements')}
            </Text>
            <EmployeeAgreementMetadata metadata={metadata} />
          </>
        )}
        {metadata.__typename === 'LeaseMetadataType' && (
          <>
            <Text size="sm" fw={600}>
              {t('DueDiligence:PageDueDiligence.tabLeases')}
            </Text>
            <LeaseMetadata metadata={metadata} />
          </>
        )}
      </Stack>
    </Box>
  );
}

export default SectionMetadata;
